import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Eraser, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const PRESET_DAYS = [5, 10, 15, 30];

const DeviceCleanupModal = ({
  open,
  onClose,
  API,
  isDark,
  mode = "device",
  device = null,
  companyId = null,
  companyName = "",
  onSaved,
}) => {
  const [enabled, setEnabled] = useState(true);
  const [deleteDays, setDeleteDays] = useState("10");
  const [customDays, setCustomDays] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logs, setLogs] = useState([]);

  const title = useMemo(() => {
    if (mode === "company") {
      return `Firma Temizlik — ${companyName || `Firma #${companyId}`}`;
    }
    const code = device?.device_id || device?.id;
    return `Cihaz Temizlik — #${device?.id}${code ? ` (${code})` : ""}`;
  }, [mode, device, companyId, companyName]);

  useEffect(() => {
    if (!open) return;
    if (mode === "device" && device) {
      setEnabled(!!device.cleanup_enabled);
      const days = device.cleanup_delete_days ?? 10;
      setDeleteDays(String(days));
      if (!PRESET_DAYS.includes(days)) {
        setCustomDays(String(days));
      } else {
        setCustomDays("");
      }
    } else {
      setEnabled(true);
      setDeleteDays("10");
      setCustomDays("");
    }
  }, [open, mode, device]);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = new URLSearchParams({ limit: "30" });
      if (mode === "device" && device?.id) {
        params.set("device_id", String(device.id));
      } else if (mode === "company" && companyId) {
        params.set("company_id", String(companyId));
      }
      const res = await axios.get(`${API}/device-cleanup-logs?${params}`);
      setLogs(res.data?.data || []);
    } catch {
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, [API, mode, device?.id, companyId]);

  useEffect(() => {
    if (open) loadLogs();
  }, [open, loadLogs]);

  const resolvedDays = () => {
    if (customDays.trim()) {
      const n = parseInt(customDays, 10);
      return Number.isFinite(n) ? n : 10;
    }
    return parseInt(deleteDays, 10) || 10;
  };

  const handleSave = async () => {
    const days = Math.max(1, Math.min(365, resolvedDays()));
    setSaving(true);
    try {
      const payload = {
        cleanup_enabled: enabled ? 1 : 0,
        delete_days: days,
      };
      if (mode === "company" && companyId) {
        payload.company_id = companyId;
      } else if (device?.id) {
        payload.device_id = device.id;
      } else {
        toast.error("Cihaz seçilemedi");
        return;
      }

      const res = await axios.post(`${API}/device-cleanup-config`, payload);
      toast.success(res.data?.message || "Temizlik ayarı kaydedildi");
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const labelClass = isDark ? "text-slate-300" : "text-gray-700";
  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";
  const inputClass = isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300";
  const logBoxClass = isDark ? "bg-slate-950/60 border-slate-800 text-slate-200" : "bg-gray-50 border-gray-200 text-gray-800";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`max-w-lg max-h-[90vh] overflow-y-auto ${isDark ? "bg-slate-900 border-slate-800 text-white" : ""}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Eraser className="w-5 h-5 text-amber-500" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className={`text-xs ${mutedClass}`}>
            Telefondaki ses kayıtları: seçilen günden eski olanlar APK tarafından silinir.
            Ayar <code className="text-[11px]">device_cleanup_config</code> tablosuna yazılır; APK bir sonraki kontrolde uygular.
          </p>

          <div className="flex items-center justify-between gap-3">
            <Label className={labelClass}>Temizlik aktif</Label>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <div>
            <Label className={labelClass}>Son kaç gün kalsın?</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {PRESET_DAYS.map((d) => (
                <Button
                  key={d}
                  type="button"
                  size="sm"
                  variant={deleteDays === String(d) && !customDays ? "default" : "outline"}
                  className={deleteDays === String(d) && !customDays ? "bg-amber-600 hover:bg-amber-700 text-white" : (isDark ? "border-slate-700" : "")}
                  onClick={() => { setDeleteDays(String(d)); setCustomDays(""); }}
                >
                  {d} gün
                </Button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Label className={`text-xs shrink-0 ${mutedClass}`}>Özel:</Label>
              <Input
                type="number"
                min={1}
                max={365}
                placeholder="ör. 30"
                value={customDays}
                onChange={(e) => setCustomDays(e.target.value)}
                className={`h-8 w-24 text-sm ${inputClass}`}
              />
              <span className={`text-xs ${mutedClass}`}>gün (1–365)</span>
            </div>
          </div>

          {mode === "company" && (
            <p className={`text-xs rounded-md border px-3 py-2 ${isDark ? "border-amber-500/30 bg-amber-500/10 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
              Bu firmaya atanmış tüm cihazlara aynı ayar uygulanır ({enabled ? `${resolvedDays()} gün kalacak` : "temizlik kapalı"}).
            </p>
          )}

          <div>
            <div className="flex items-center gap-2 mb-2">
              <History className="w-4 h-4 text-slate-400" />
              <Label className={labelClass}>Son ayar logları</Label>
              {loadingLogs && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
            </div>
            <div className={`rounded-lg border max-h-40 overflow-y-auto text-xs font-mono p-2 space-y-1.5 ${logBoxClass}`}>
              {logs.length === 0 && !loadingLogs && (
                <p className={mutedClass}>Henüz log yok</p>
              )}
              {logs.map((log) => (
                <div key={log.id} className="border-b border-dashed border-slate-700/30 pb-1 last:border-0">
                  <span className="opacity-70">{log.created_at}</span>
                  {" · "}
                  <span>{log.action}</span>
                  {log.delete_days != null && <span> · {log.delete_days}g</span>}
                  {log.affected_count > 1 && <span> · {log.affected_count} cihaz</span>}
                  {log.user_label && <span className="opacity-80"> · {log.user_label}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            İptal
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eraser className="w-4 h-4 mr-2" />}
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceCleanupModal;
